import { useQuery } from '@tanstack/react-query';

export interface LeaderboardStore {
  rank: number;
  store_name: string;
  total_redemptions: number;
}

export interface LeaderboardData {
  division: string;
  updated_at: string;
  is_active: boolean;
  page: number;
  per_page: number;
  total_pages: number;
  total_items: number;
  stores: LeaderboardStore[];
}

interface UseLeaderboardResult {
  data: LeaderboardData | undefined;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

async function fetchLeaderboard(
  division: string,
  page: number,
  perPage: number
): Promise<LeaderboardData> {
  const url = `/.netlify/functions/api-leaderboard?division=${encodeURIComponent(
    division
  )}&page=${page}&per_page=${perPage}`;

  const response = await fetch(url);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `HTTP error! status: ${response.status}`
    );
  }

  return response.json();
}

export function useLeaderboard(
  division: string,
  page: number = 1,
  perPage: number = 100
): UseLeaderboardResult {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['leaderboard', division, page, perPage],
    queryFn: () => fetchLeaderboard(division, page, perPage),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  return {
    data,
    loading: isLoading,
    error: error ? (error as Error).message : null,
    refetch: () => { refetch(); },
  };
}
